import styles from "./EventItem.module.scss";
import { IPlannerEventModel } from "../models/plannerEventModel";
import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { Tag } from "../../../../common/components/common/Tag/Tag";
import { EventTypeLabels } from "../../../../common/labels";
import { EventType } from "../../../../common/enums";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../../../common/infrastructure/components/RenderWhen";
import {
  Default_Instructor_Color,
  generateHexFromString,
  brightenForDarkBackground,
} from "../../../../common/infrastructure/utilities/colorUtility";
import { getGroupTypeIndicatorClass } from "./utils/EventItemUtility";
import { useAppContext } from "../../../../AppContext";
import { LOCKED_EVENT_MESSAGE } from "../hooks/usePlannerEventClick";
import {
  formatDuration,
  getEventCalendarTimeLabel,
  isMultiDayDuration,
} from "../../../../common/infrastructure/utilities/durationUtility";
import { isMultisportAcceptedForEvent } from "../../../../common/utilities/paymentUtility";
import { MultisportTag } from "../../../../common/components/common/MultisportTag/MultisportTag";

interface IEventItemProps {
  className?: string;
  item?: IPlannerEventModel;
  isLoading: boolean;
  onEventClick: (item: IPlannerEventModel) => void;
  actionButtons?: React.ReactNode;
}

export const EventItem = ({
  className,
  item,
  isLoading,
  actionButtons,
  onEventClick,
}: IEventItemProps) => {
  const { selectedStudio } = useAppContext();
  const showHallTag =
    !!selectedStudio?.tenantUsesMoreThanOneHall && !!item?.hallName;
  // "Will not take place" — distinct from isCancelled (which mirrors soft-deletion)
  // and from BlockedTime. Rendered crossed-out, like the user portal calendar.
  const isNotHappening =
    !!item?.willNotTakePlace &&
    !item?.isCancelled &&
    item?.type !== EventType.BlockedTime;
  // Only classes that will take place; blocked time never takes MultiSport.
  const showMultisportTag =
    item?.type !== EventType.BlockedTime &&
    !item?.isCancelled &&
    !isNotHappening &&
    isMultisportAcceptedForEvent(
      item?.isMultisportAvailable,
      selectedStudio?.supportedPaymentTypes,
    );
  const isLocked = !!item?.isLocked;
  let freeSpacesIcon: JSX.Element;

  const isClassFull =
    item?.takenSpaces &&
    item?.totalSpaces &&
    item?.takenSpaces >= item?.totalSpaces;

  const freeSpacesColor =
    !item?.hasLimitForAvailableSpaces || isClassFull ? "#5dffb6" : "#e884ff";

  if (!item?.hasLimitForAvailableSpaces) {
    freeSpacesIcon = (
      <Icon
        type={IconType.CheckmarkSentIcon}
        height={15}
        color={freeSpacesColor}
      />
    );
  } else if (isClassFull) {
    freeSpacesIcon = (
      <Icon
        type={IconType.TwoCheckmarksSeenIcon}
        height={15}
        color={freeSpacesColor}
      />
    );
  } else {
    freeSpacesIcon = (
      <Icon
        type={IconType.TwoCheckmarksDeliveredIcon}
        height={15}
        color={freeSpacesColor}
      />
    );
  }

  const typeOfEvent = EventTypeLabels[item?.type ?? EventType.Group];
  const groupTypeIndicatorClass = getGroupTypeIndicatorClass(item?.type);

  const instructorColor = brightenForDarkBackground(
    item?.employeeColor
      ? generateHexFromString(item?.employeeColor)
      : generateHexFromString(Default_Instructor_Color)
  );

  return (
    <div className={classNames(styles.root_indicator, groupTypeIndicatorClass)}>
      <div
        className={classNames(styles.root, className, {
          [styles.root_cancelled]: item?.isCancelled,
          [styles.root_passed]:
            item?.isPassed && !item?.isCancelled && !isNotHappening,
          [styles.root_blocked]: item?.type === EventType.BlockedTime,
          [styles.root_not_happening]: isNotHappening,
          [styles.root_locked]: isLocked,
        })}
        onClick={() =>
          // Blocked time doesn't open; a locked one still explains why it has no actions.
          item?.id && (item.type !== EventType.BlockedTime || isLocked)
            ? onEventClick(item)
            : () => {}
        }
      >
        <div className={styles.root_left_section}>
          <SkeletonItem
            isLoading={isLoading}
            width={400}
            value={
              <div className={styles.root_left_section_title}>
                <span
                  className={classNames({
                    [styles.root_left_section_title_struck]: isNotHappening,
                  })}
                >
                  {item?.name}
                </span>
                <span className={styles.root_left_section_title_subtitle}>
                  {typeOfEvent}
                  {!item?.showInUserPortal &&
                    (item?.type === EventType.Group ||
                      item?.type === EventType.Individual) && (
                      <Icon
                        className={styles.root_title_icon_btn}
                        type={IconType.CrossedOutEye}
                        height={15}
                      />
                    )}

                  {item?.isRecurring && (
                    <Icon
                      className={styles.root_title_icon_btn}
                      type={IconType.RecurringIcon}
                      height={15}
                    />
                  )}
                </span>
              </div>
            }
          />
          <SkeletonItem
            isLoading={isLoading}
            width={400}
            value={
              <div
                className={classNames(
                  styles.root_left_section_item,
                  styles.root_left_section_item_sub
                )}
              >
                <Tag
                  className={styles.tag}
                  label={item?.employee ?? ""}
                  backgroundColor={instructorColor}
                  tooltip="Служител"
                />
                <RenderWhen condition={showHallTag}>
                  <Tag
                    className={styles.tag}
                    label={item?.hallName ?? ""}
                    backgroundColor={"#c4b5fd"}
                    tooltip="Зала"
                  />
                </RenderWhen>
                <Tag
                  backgroundColor={"#ff9d5c"}
                  tooltip={
                    isMultiDayDuration(item?.durationInMinutes)
                      ? `Започва в ${item?.startTime}, продължава ${formatDuration(
                          item?.durationInMinutes,
                        )}`
                      : item?.bufferAfterMinutes
                        ? `Служителят остава зает още ${formatDuration(
                            item.bufferAfterMinutes,
                          )} след резервацията (буфер на услугата)`
                        : undefined
                  }
                  label={
                    <span className={styles.tag}>
                      {getEventCalendarTimeLabel({
                        startTime: item?.startTime,
                        endTime: item?.endTime,
                        durationInMinutes: item?.durationInMinutes,
                        bufferAfterMinutes: item?.bufferAfterMinutes,
                      })}
                    </span>
                  }
                />

                <RenderWhen condition={isNotHappening}>
                  <Tag
                    className={styles.tag}
                    backgroundColor={"#ff8186"}
                    label="Този клас няма да се проведе"
                  />
                </RenderWhen>

                <RenderWhen condition={item?.type === EventType.Group && !item?.isCancelled && !isNotHappening}>
                  <Tag
                    backgroundColor={freeSpacesColor}
                    tooltip="Заети места / максимален брой места"
                    label={
                      <span className={styles.tag}>
                        {freeSpacesIcon}
                        <span
                          className={classNames({
                            [styles.root_left_section_item_spaces_text_success]:
                              !item?.hasLimitForAvailableSpaces || isClassFull,
                          })}
                        >
                          {item?.takenSpaces}/{item?.totalSpaces ?? "∞"}
                        </span>
                      </span>
                    }
                  />
                </RenderWhen>
                <RenderWhen
                  condition={
                    item?.type !== EventType.Group && !!item?.serviceUser
                  }
                >
                  <Tag
                    className={styles.tag}
                    label={item?.serviceUser ?? ""}
                    backgroundColor={"#4dd4cb"}
                    tooltip="Клиент"
                  />
                </RenderWhen>

                <RenderWhen condition={!!item?.waitingCount}>
                  <Tag
                    className={styles.tag}
                    label={
                      <span className={styles.tag}>
                        <Icon
                          type={IconType.ClockIcon}
                          height={15}
                          color={"#ffb3b3"}
                        />
                        {item?.waitingCount}
                      </span>
                    }
                    backgroundColor={"#ffb3b3"}
                    tooltip="Потребители в изчакващи"
                  />
                </RenderWhen>

                <RenderWhen condition={showMultisportTag}>
                  <MultisportTag className={styles.tag} />
                </RenderWhen>
              </div>
            }
          />
        </div>
        <div className={styles.root_right_section}>
          {actionButtons}
          <RenderWhen condition={isLocked || item?.type !== EventType.BlockedTime}>
            <IfTrue>
              {isLocked ? (
                <span
                  className={styles.root_lock}
                  title={LOCKED_EVENT_MESSAGE}
                  aria-label={LOCKED_EVENT_MESSAGE}
                >
                  <Icon type={IconType.Lock} height={18} width={18} />
                </span>
              ) : (
                <Icon type={IconType.RightChevronIcon} />
              )}
            </IfTrue>
            <IfFalse>
              <div style={{ width: "20px" }} />
            </IfFalse>
          </RenderWhen>
        </div>
      </div>
    </div>
  );
};
